import React, { useState } from "react";

import { Stack } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";

import { MIN_PORTFOLIO_NAME_LENGTH } from "../CreateCustomOrPartnerModelModal/CreateCustomOrPartnerModelModal";
import { taxTypeOptions } from "../PortfolioBuilder/constants";
import { ModelDetails, TaxTypeEnum } from "../PortfolioBuilder/reducer/Types";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import { DropdownMenuOption } from "@src/sharedComponents/CustomDropdownBase/MenuOptionsWithCheckIfSelected/MenuOptionsWithCheckIfSelected";
import BaseDropdown from "@src/sharedComponents/Dropdown/BaseDropdown";
import FormTextField from "@src/sharedComponents/Forms/FormTextField";
import ModalWithClearButton from "@src/sharedComponents/Modal/ModalWithClearButton";
import TaxStatusLabel from "@src/sharedComponents/TaxStatusLabel/TaxStatusLabel";
import Text from "@src/sharedComponents/Typography/Text";

import styles from "./CreateCustomModelModal.module.css";

interface Form {
  TextInput: string;
}

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (modelDetails: ModelDetails) => void;
  currentModelName?: string;
  currentTaxType?: TaxTypeEnum;
};

const CreateCustomModelModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSubmit,
  currentModelName,
  currentTaxType,
}): JSX.Element => {
  const [taxTypeOption, setTaxTypeOption] = useState<
    DropdownMenuOption<TaxTypeEnum>
  >(taxTypeOptions.find((el) => el.id === currentTaxType) ?? taxTypeOptions[0]);

  const notify = useStatusNotification();

  const methods = useForm<Form>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      TextInput: currentModelName ?? "",
    },
  });

  const {
    getValues,
    formState: { isValid, isDirty },
  } = methods;

  const onSave = async () => {
    if (!isValid) {
      return;
    }

    const values = getValues();

    if (values.TextInput.length < MIN_PORTFOLIO_NAME_LENGTH) {
      notify("Portfolio name must have at least 3 characters", "Error");
      return;
    }

    onSubmit({
      modelName: values.TextInput,
      taxType: taxTypeOption.id,
      basePermission: "Private",
    });
  };

  const onSetTaxTypeOption = (tto: DropdownMenuOption<TaxTypeEnum>) =>
    setTaxTypeOption(tto);

  return (
    <ModalWithClearButton closeModal={onClose} isOpen={isOpen}>
      <Text className={styles.title} variant="title2">
        Set up the model
      </Text>

      <FormProvider {...methods}>
        <FormTextField name="TextInput" label={"Name"} placeholder={""} />

        <Text className={styles.label} variant="bodySmall" color={"textSubtle"}>
          Must be a unique name
        </Text>
      </FormProvider>

      {currentTaxType !== "DirectIndexedSleeve" && (
        <BaseDropdown
          title={<TaxStatusLabel />}
          curItemLabel={taxTypeOption.label}
          options={taxTypeOptions.filter(
            // don't allow DirectIndexedSleeve for now
            (el) => el.id !== "DirectIndexedSleeve"
          )}
          onSelect={onSetTaxTypeOption}
          hideCheckmark={true}
        />
      )}

      <Stack
        spacing={2}
        direction={"row"}
        marginTop={"40px"}
        justifyContent={"flex-end"}
      >
        <Button
          text="Cancel"
          buttonType="primary"
          variant="text"
          disabled={false}
          onClick={onClose}
        />

        <Button
          text="Continue"
          buttonType="primary"
          variant="contained"
          disabled={!isValid || !isDirty}
          onClick={onSave}
        />
      </Stack>
    </ModalWithClearButton>
  );
};

export default CreateCustomModelModal;
